import {
  action,
  computed,
  configure,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import {
  BaseUserCustomProperties,
  coreapi,
  User,
  UserProfileUpdate,
} from "services/coreapi";

configure({ enforceActions: "always" });

/**
 * Brand-agnostic user model. Apps subclass it with their own custom
 * properties type (and brand-specific behavior):
 *
 *   class MyAppUserModel extends UserModel<MyAppCustomProperties> {}
 *
 * `makeObservable` (not `makeAutoObservable`) is used on purpose — MobX
 * forbids `makeAutoObservable` in classes that get subclassed. A subclass
 * adding its own observable members must call `makeObservable` again with
 * annotations for just those members.
 */
export class UserModel<
  P extends BaseUserCustomProperties = BaseUserCustomProperties,
> {
  data: User<P>;

  constructor(data: User<P>) {
    this.data = data;
    makeObservable(this, {
      data: observable,
      hasCompletedOnboarding: computed,
      hasActiveSubscription: computed,
      subscriptionScheduledForCancellation: computed,
      hasUsedFreeMonth: computed,
      canOfferFreeMonth: computed,
      isFreeMonthActive: computed,
      subscriptionEndsAt: computed,
      isMembershipManageable: computed,
      hasGrantedAgeConsent: computed,
      hasPassword: computed,
      setHasCompletedOnboarding: action,
      updateProfile: action,
      updateCustomProperties: action,
      refresh: action,
      markPasswordSet: action,
    });
  }

  /**
   * Re-fetch the user from the server and swap the data in place. Keeps the
   * model instance stable, so stores holding a reference to it (e.g. an app
   * RootStore) observe the new state.
   */
  refresh = async () => {
    const user = await coreapi.getMe<P>();

    runInAction(() => {
      this.data = user;
    });
  };

  get hasCompletedOnboarding(): boolean {
    return this.data.custom_properties["has-completed-onboarding"] === true;
  }

  get hasActiveSubscription(): boolean {
    return this.data.subscription?.status === "active";
  }

  get subscriptionScheduledForCancellation(): boolean {
    return this.data.subscription?.cancelAtPeriodEnd === true;
  }

  /**
   * Whether the user already took the free month ("postpone next bill")
   * offer. The server allows it once per subscription, so a used offer must
   * never be shown again.
   */
  get hasUsedFreeMonth(): boolean {
    return this.data.subscription?.trialExtendedAt != null;
  }

  /** Whether the free-month offer may be presented in the cancel flow. */
  get canOfferFreeMonth(): boolean {
    return (
      this.hasActiveSubscription &&
      !this.subscriptionScheduledForCancellation &&
      !this.hasUsedFreeMonth
    );
  }

  /**
   * Whether the free month is currently running. `trial_extended_at` stays
   * set forever once used, but the first bill after the postponement starts
   * a new billing period — so the free month is over once the current period
   * begins clearly *after* the extension moment. "Clearly" = more than a day
   * later: the postpone itself may re-sync period timestamps moments after
   * the extension is recorded (the server claims `trial_extended_at` before
   * calling the payment provider), and that jitter must not count as a bill.
   */
  get isFreeMonthActive(): boolean {
    const subscription = this.data.subscription;
    if (!this.hasActiveSubscription || !subscription?.trialExtendedAt) {
      return false;
    }

    const DAY_MS = 24 * 60 * 60 * 1000;
    const extendedAt = new Date(subscription.trialExtendedAt).getTime();
    const periodStart = subscription.currentPeriodStart
      ? new Date(subscription.currentPeriodStart).getTime()
      : 0;

    return periodStart <= extendedAt + DAY_MS;
  }

  /**
   * When a subscription scheduled for cancellation stops for good: the
   * provider's `cancelAt`, falling back to the end of the current period.
   * Null unless the subscription is active and scheduled for cancellation.
   */
  get subscriptionEndsAt(): Date | null {
    const subscription = this.data.subscription;
    if (
      !this.hasActiveSubscription ||
      !this.subscriptionScheduledForCancellation
    ) {
      return null;
    }
    const raw = subscription?.cancelAt ?? subscription?.currentPeriodEnd;
    return raw ? new Date(raw) : null;
  }

  /**
   * Whether the user can act on their membership (manage / cancel). Only an
   * active subscription that isn't already scheduled for cancellation and
   * isn't in the free month.
   */
  get isMembershipManageable(): boolean {
    return (
      this.hasActiveSubscription &&
      !this.subscriptionScheduledForCancellation &&
      !this.isFreeMonthActive
    );
  }

  get hasGrantedAgeConsent(): boolean {
    return this.data.ageConsent?.consented === true;
  }

  /**
   * Unknown counts as yes: an older core API doesn't send the field, and
   * asking everyone for a password it cannot save would lock them all out.
   */
  get hasPassword(): boolean {
    return this.data.hasPassword !== false;
  }

  /** The server confirmed a save; don't wait on a refresh that may fail. */
  markPasswordSet = () => {
    this.data = { ...this.data, hasPassword: true };
  };

  /** Resolves `false` when the flag could not be saved (see below). */
  setHasCompletedOnboarding = async (value: boolean = true) => {
    return this.updateCustomProperties({
      "has-completed-onboarding": value,
    } as Partial<P>);
  };

  /**
   * Persist typed profile columns (gender, attracted_to) on the server.
   * Unlike `updateCustomProperties` this rejects on failure — callers are
   * user-facing edits that should be able to report it.
   */
  updateProfile = async (profile: UserProfileUpdate) => {
    const user = await coreapi.updateUser<P>({ id: this.data.id, ...profile });

    runInAction(() => {
      // Merge, same as updateCustomProperties: the update response omits the
      // project-scoped `subscription` field and must not wipe it.
      this.data = { ...this.data, ...user };
    });
  };

  /** Persist a partial set of the app's custom properties on the server.
   *
   * Never rejects: resolves `true` on success and `false` when the write
   * failed (offline, server error) — local state is then left untouched,
   * so callers that gate navigation on the flag must check the result.
   */
  updateCustomProperties = async (customProperties: Partial<P>) => {
    try {
      const user = await coreapi.updateUser<P>({
        id: this.data.id,
        custom_properties: customProperties,
      });

      runInAction(() => {
        // Merge instead of replace: a field the response omits entirely
        // (absent key, not an explicit null) must not wipe local state.
        this.data = { ...this.data, ...user };
      });
      return true;
    } catch (err) {
      console.error("Failed to update user custom properties:", err);
      return false;
    }
  };
}
